import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	Pagination,
	PaginationHeader,
	PaginationLoader,
	SendEmailModal,
	PopupSnackbar,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import * as fromHealthSafetyForm from "features/health-safety-forms";
import AccidentCard from "../cards/accident-card";
import { AppendIncidentForm, EditIncidentForm, PageHeaderTabs } from "features/health-safety-forms";
import { HOME_ROUTE } from "features/home/home-routes";
import { getPageResults, useIsOnline, useTerminology } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialJobState } from "features/health-and-safety/reducers/health-safety-reducer";
import { CardText } from "features/timesheet/components/timesheet-list";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyles = makeStyles((theme) => ({
	root: {},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const IncidentList = ({
	incidents,
	onDelete,
	profile,
	currentOrganisationGUID,
	onSearchChanged
	// searchFilter,
	// onGetIncidentList
}) => {
	const isOnline = useIsOnline();
	const classes = useStyles();
	const { siteLabel } = useTerminology();
	const [showAppendModal, setShowAppendModal] = useState(false);
	const [showViewModal, setShowViewModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const [siteId, setSiteId] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	const [incidentList, setIncidentList] = useState(getPageResults(initialJobState));
	const [currentPage, setCurrentPage] = useState(1);
	const defaultPageSize = 10; // Reduced from 20 to improve initial load performance
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const [hasMoreIncidents, setHasMoreIncidents] = useState(true);
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const ref = useRef(null);
	const [height, setHeight] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);

	// const incidentList = getPageResults(incidents);
	const empty = incidentList && incidentList.length <= 0;

	const onAppend = (id) => () => {
		setSelectedItemId(id);
		setShowAppendModal(true);
	};
	const onView = (id) => () => {
		setSelectedItemId(id);
		setShowViewModal(true);
	};
	const onEdit = (id) => () => {
		setSelectedItemId(id);
		setShowEditModal(true);
	};
	const onEmail = (id, siteId) => () => {
		setSelectedItemId(id);
		setSiteId(siteId);
		setShowEmailModal(true);
	};
	const handleApiError = (error) => {
		if (error.response && error.response.status === "401") {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
		} else {
			setErrorMessage("Something went wrong, please refresh the page and try again.");
		}
	};
	const isFetching = incidents.flag === "processing" && isOnline;
	// const isDoneFetching = incidents.flag === "done" || !isOnline;

	const setNewIncidentState = (incidentResult, page) => {
		let newState = initialJobState;

		if (incidentResult && incidentResult.results && incidentResult.results.length > 0) {
			newState = {
				...newState,
				...incidentResult
			};
		}

		newState.refreshList = incidentResult.refreshList;
		newState.flag = "done";
		// If navigating to first page clear the resuls from incidentList otherwise add to the end of it
		if (page === 1) {
			setIncidentList([...getPageResults(newState)]);
		} else {
			setIncidentList([...incidentList, ...getPageResults(newState)]);
		}
		setIsDoneFetching(true);
	};

	const getIncidents = (
		fromPageToRequest = currentPage, // fromPageToRequest is the page to request from the API,
		recordDeletedOrUpdated = false, // recordDeletedOrUpdated is a boolean to determine whether to overwrite or append to incidentLists
		pageSize = defaultPageSize // pageSize is the number of records to load from the API, can be paramaterised to request more data.
	) => {
		if (totalNumberOfRecords !== incidentList.length || recordDeletedOrUpdated) {
			if (recordDeletedOrUpdated) {
				setHasMoreIncidents(true);
			}

			safetyWiseApi
				.post(`${currentOrganisationGUID}/accident/paged`, {
					ascending: false,
					page: fromPageToRequest,
					pageSize,
					orderBy: "createdAt",
					searchKey: searchFilter
				})
				.then((result) => {
					setNewIncidentState(result.data, fromPageToRequest);

					// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
					if (pageSize > defaultPageSize && fromPageToRequest === 1) {
						setCurrentPage(pageSize / defaultPageSize);
					} else {
						setCurrentPage(fromPageToRequest);
					}

					setTotalNumberOfRecords(result.data.totalNumberOfRecords);
					if (
						result.data.totalNumberOfRecords ==
						incidentList.length + result.data.results.length
					) {
						setHasMoreIncidents(false);
					}
				})
				.catch((error) => {
					console.error("Error fetching incidents:", error);
					setErrorMessage("Failed to load incidents. Please try again.");
					setIsDoneFetching(true);
					setHasMoreIncidents(false);
				});
		} else {
			setHasMoreIncidents(false);
		}
	};

	// The InfiniteScroll Component requires a height for it to work  effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			setHeight(ref.current.offsetHeight - 20); // reduce height by 20px ottherwise the scrollbar flows past the height of screen.
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Initial load - wait for currentOrganisationGUID to be available
	useEffect(() => {
		if (currentOrganisationGUID) {
			getIncidents();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganisationGUID]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setIncidentList([]);
			setTotalNumberOfRecords(1);
			setHasMoreIncidents(true);
			setIsDoneFetching(false);
			getIncidents(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter]);

	return (
		<Page className={classes.root} title="Incidents" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				{/* <p>
					{totalNumberOfRecords} {incidentList.length}
				</p> */}
				{/* n.o of records and length of loaded list */}
				<InfiniteScroll
					dataLength={incidentList.length}
					next={() => {
						getIncidents(currentPage + 1);
					}}
					hasMore={hasMoreIncidents}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					<ScrollToTopOnMount />

											<PageHeaderTabs
							title="Incidents"
							addTabPath={fromHealthSafetyForm.ADD_INCIDENT_PATH}
							hideListTabButton={true}
							backActionPath={HOME_ROUTE}
						
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchFilter && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																	setSearchFilter("");
																	setCurrentPage(1);
																	setIncidentList([]);
																	setTotalNumberOfRecords(1);
																	setHasMoreIncidents(true);
																	setIsDoneFetching(false);
																	getIncidents(1, true);
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchFilter}
												onChange={(val) => {
													const { value } = val.target;
													setSearchFilter(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={`e.g. ${siteLabel} Name, Incident Details, Category`}
											/>
										</Grid>
									</Grid>
								</div>
							)}
							{/* END: Search */}
						</PageHeaderTabs>
					<PageContent pad>
						{/* <PaginationHeader pagination={incidents} /> */}
						<p style={{ marginTop: 5 }}>
							{incidentList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
						</p>

						{!empty &&
							isDoneFetching &&
							incidentList.map((item, index) => (
								<AccidentCard
									key={index}
									accident={item}
									role={profile.role}
									onAppend={onAppend(item.id)}
									onEdit={onEdit(item.id)}
									onView={onView(item.id)}
									onDelete={() =>{
										//incidentIndex + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
										const pageSizeToLoad =
										Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;

										safetyWiseApi
											.del(`${currentOrganisationGUID}/accident/delete/${item.id}`)
											.then(() => {
												getIncidents(1, true, pageSizeToLoad);
											})}
									}
									onEmail={onEmail(item.id, item.siteId)}
								/>
							))}

						{isFetching && <PaginationLoader height={90} pagination={incidents} />}

						{empty && isDoneFetching && (
							<Typography variant="body1">There are currently no Incidents listed.</Typography>
						)}

						{/* <Pagination
							onGetList={onGetIncidentList}
							pagination={incidents}
							orderBy="createdAt"
							ascending={false}
							searchKey={searchFilter}
						/> */}
					</PageContent>

					{showAppendModal && (
						<AppendIncidentForm
							id={selectedItemId}
							values={incidentList.find((x) => x.id == selectedItemId)}
							incidentList={incidentList}
							onClose={(submitted = false) => {
								setShowAppendModal(false);
								if (submitted) {
									console.log("get here");
									const incidentIndex = incidentList.findIndex((t) => t.id === selectedItemId); // find incidentIndex
									//incidentIndex + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
									const pageSizeToLoad =
										Math.ceil((incidentIndex + 1) / defaultPageSize) * defaultPageSize;

									getIncidents(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					{showViewModal && (
						<EditIncidentForm
							readOnly
							id={selectedItemId}
							incidentList={incidentList}
							onClose={() => setShowViewModal(false)}
						/>
					)}
					{showEditModal && (
						<EditIncidentForm
							id={selectedItemId}
							incidentList={incidentList}
							onClose={(submitted = false) => {
								setShowEditModal(false);
								if (submitted) {
									const incidentIndex = incidentList.findIndex((t) => t.id === selectedItemId);
									const pageSizeToLoad =
										Math.ceil((incidentIndex + 1) / defaultPageSize) * defaultPageSize;
									getIncidents(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					<SendEmailModal
						isOpen={showEmailModal}
						title={"Email Incident Record"}
						onClose={() => setShowEmailModal(false)}
						onSend={(selectedRecipients) => {
							setIsSendingEmail(true);
							safetyWiseApi
								.post(`${currentOrganisationGUID}/accident/email/${selectedItemId}`, {
									toInternalAddresses: selectedRecipients.recipients,
									toExternalAddresses: [
										...selectedRecipients.recipientsOther,
										...selectedRecipients.recipientsCustomerSites
									]
								})
								.then(() => {
									setSuccessMessage("Emails sent successfully.");
								})
								.catch(handleApiError)
								.finally(() => {
									setIsSendingEmail(false);
									setShowEmailModal(false);
								});
						}}
						isSaving={isSendingEmail}
						selectedItemId={selectedItemId}
						siteId={siteId}
						emailListType={"relatedToParticularSite"}
					/>
					<PopupSnackbar
						message={errorMessage}
						coloured
						error
						onExited={() => setErrorMessage("")}
					/>
					<PopupSnackbar
						message={successMessage}
						coloured
						success
						onExited={() => setSuccessMessage("")}
					/>
					{!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!hasMoreIncidents && <CardText message={"All incidents have been loaded."} />}
					{incidentList.length === 0 && isDoneFetching && (
						<CardText message={"No incidents to show."} />
					)}
				</InfiniteScroll>
			</div>
		</Page>
	);
};

IncidentList.propTypes = {
	incidents: PropTypes.object.isRequired,
	searchFilter: PropTypes.string,
	onDelete: PropTypes.func.isRequired,
	profile: PropTypes.object.isRequired,
	supervisor: PropTypes.string,
	onSearchChanged: PropTypes.func.isRequired,
	onGetIncidentList: PropTypes.func.isRequired
};

export default IncidentList;
